import { cva } from "cva";
import Image from "next/image";
import { BuildHtml } from "~/components/BuildHtml";
import {
  HeaderBase,
  HeaderBaseProps,
} from "~/landing-pages/components/Header/HeaderBase/HeaderBase";
import { headerContainerStyles } from "~/landing-pages/components/Header/header-shared-styles";
import { LandingPageCard } from "~/landing-pages/landing-page-types";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body } from "~/ui-library";

export type HeaderWithCardsProps = HeaderBaseProps & {
  cards: LandingPageCard[];
  imageUrl?: string;
  textLocation?: HeaderBaseProps["textLocation"];
  textAlignment?: HeaderBaseProps["textAlignment"];
};

const cardContainerStyle = cva("flex gap-x-4 rounded-xl  p-6 shadow-0", {
  variants: {
    appearance: {
      brand: "bg-white/10",
      dark: "bg-white/10",
      light: "bg-white",
      dark_image: "bg-white/5",
      light_image: "bg-white",
    },
  },
});

const cardNameStyle = cva("", {
  variants: {
    appearance: {
      brand: "text-white",
      dark: "text-white",
      light: "text-gray-900",
      dark_image: "text-white",
      light_image: "text-gray-900",
    },
  },
});

const cardDescriptionStyle = cva("mt-2", {
  variants: {
    appearance: {
      brand: "text-gray-100",
      dark: "text-gray-100",
      light: "text-gray-700",
      dark_image: "text-gray-100",
      light_image: "text-gray-700",
    },
  },
});

export const HeaderWithCards: React.FC<HeaderWithCardsProps> = ({
  appearance,
  cards,
  imageUrl,
  ...rest
}) => (
  <div
    className={headerContainerStyles({
      appearance,
      className: "relative isolate overflow-hidden",
    })}
  >
    {imageUrl ? (
      <Image
        // being a decorative image, this should not have alt text
        alt=""
        src={imageUrl}
        className="absolute inset-0 -z-10 size-full object-cover"
        fill
      />
    ) : null}
    <div
      className="hidden sm:absolute sm:-top-10 sm:right-1/2 sm:-z-10 sm:mr-10 sm:block sm:transform-gpu sm:blur-3xl"
      aria-hidden="true"
    >
      <div
        className="aspect-[1097/845] w-[68.5625rem] bg-gradient-to-tr from-brand to-brand-indigo opacity-20"
        style={{
          clipPath:
            "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
        }}
      />
    </div>
    <div
      className="absolute -top-52 left-1/2 -z-10 -translate-x-1/2 transform-gpu blur-3xl sm:top-[-28rem] sm:ml-16 sm:translate-x-0 sm:transform-gpu"
      aria-hidden="true"
    >
      <div
        className="aspect-[1097/845] w-[68.5625rem] bg-gradient-to-tr from-brand to-brand-indigo opacity-20"
        style={{
          clipPath:
            "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
        }}
      />
    </div>
    <div>
      <HeaderBase appearance={appearance} {...rest} />
    </div>

    <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-6 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3 lg:gap-8">
      {cards.map((card) => (
        <div key={card.name} className={cardContainerStyle({ appearance })}>
          <div>
            <Body as="h3" bold className={cardNameStyle({ appearance })}>
              {card.name}
            </Body>
            <Body className={cardDescriptionStyle({ appearance })}>
              <BuildHtml
                html={card.description}
                tagToClassNameMap={tagToClassNameMap}
              />
            </Body>
          </div>
        </div>
      ))}
    </div>
  </div>
);
